import { motion, useMotionValueEvent, useScroll } from "framer-motion";
import { useState } from "react";

import { Mark } from "./Mark";

/**
 * Page header — wordmark + inline menu + CTA.
 *
 * Menu: Practice / Events / Contact. Practice and Contact are
 * on-page anchors (work from the home page; from other routes they
 * jump home + scroll). Events is the /events index.
 *
 * `tone` handles the hero underneath: "light" (landing — dark footage
 * behind, ivory text until scrolled), "dark" (paper pages — ink text
 * throughout).
 */
export function Nav({ tone = "dark" }: { tone?: "light" | "dark" }) {
  const [scrolled, setScrolled] = useState(false);
  const { scrollY } = useScroll();

  useMotionValueEvent(scrollY, "change", (latest) => {
    setScrolled(latest > 80);
  });

  const onDarkHero = tone === "light" && !scrolled;
  const linkClass = onDarkHero
    ? "text-warm-white/85 hover:text-warm-white"
    : "text-warm-brown-light hover:text-warm-brown";
  const wordmarkClass = onDarkHero ? "text-warm-white" : "text-warm-brown";
  const ctaClass = onDarkHero
    ? "border border-warm-white/45 text-warm-white hover:border-warm-white hover:bg-warm-white hover:text-warm-brown"
    : "border border-warm-brown bg-warm-brown text-cream hover:bg-warm-brown-light";

  return (
    <motion.nav
      className={`fixed top-0 right-0 left-0 z-40 transition-all duration-500 ${
        scrolled
          ? "hairline-b bg-cream/95 backdrop-blur-md"
          : "border-b border-transparent bg-transparent"
      }`}
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.15, ease: "easeOut" }}
    >
      <div className="flex items-center justify-between px-7 py-4 md:px-14 lg:px-20">
        <a
          href="/"
          aria-label="Vihangam Yog New Zealand — home"
          className={`flex items-center gap-2.5 ${wordmarkClass}`}
        >
          <Mark size={30} />
          <span className="hidden text-[0.7rem] font-semibold tracking-[0.3em] uppercase sm:inline">
            Vihangam Yog
          </span>
        </a>

        <div className="hidden items-center gap-8 md:flex">
          <a
            href="/#practice"
            className={`text-[0.8rem] font-medium tracking-wide transition-colors duration-300 ${linkClass}`}
          >
            Practice
          </a>
          <a
            href="/events"
            className={`text-[0.8rem] font-medium tracking-wide transition-colors duration-300 ${linkClass}`}
          >
            Events
          </a>
          <a
            href="/contact"
            className={`text-[0.8rem] font-medium tracking-wide transition-colors duration-300 ${linkClass}`}
          >
            Contact
          </a>
        </div>

        <div className="flex items-center gap-4 md:gap-6">
          <a
            href="/events"
            className={`text-[0.8rem] font-medium tracking-wide transition-colors duration-300 md:hidden ${linkClass}`}
          >
            Events
          </a>
          <a
            href="/events"
            className={`group inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-[0.8rem] font-semibold tracking-wide transition-all duration-300 md:px-6 ${ctaClass}`}
          >
            See all events
            <span
              className="transition-transform duration-300 group-hover:translate-x-0.5"
              aria-hidden
            >
              →
            </span>
          </a>
        </div>
      </div>
    </motion.nav>
  );
}
